import React, { Component } from 'react';
import PropTypes from 'prop-types';
import SrText from 'client/components/SrText';
import { Formik, Form } from 'formik';
import lodashCompact from 'lodash/fp/compact';

import { createInitialValues, createFormValidationFn } from 'client/helpers/formikHelpers';
import momentWithAustinTimezone from 'common/util/momentWithAustinTimezone';
import {
  SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
} from 'api/actionTypes';
import postWithState from 'client/actions/postWithState';
import SrButton, {
  SR_BUTTON_THEME_ADOBE_GREY,
  SR_BUTTON_THEME_ADOBE_BLUE,
} from 'client/components/SrButton/SrButton';
import SrModalV2, { MODAL_THEME_STACK } from 'client/components/SrModalV2/SrModalV2';
import SrIcon from 'client/components/SrIcon';
import SrFlex from 'client/components/SrFlex';
import SrBox from 'client/components/SrBox';
import SrFormikField, { SR_FIELD_THEME_ADOBE, SR_FIELD_THEME_PARTNER_FORM } from './SrFormikField';

function makeFormFieldsGetterFunc({
  googleAddressMatches,
  onGoogleAddressChange,
  onFindGoogleListings,
}) {
  return ({ values = {} } = {}) => {
    return [
      {
        id: 'addressStreetDisplay',
        title: 'Address',
        type: 'autocomplete',
        validations: lodashCompact([
          'required',
          !values.matchAddress && 'selectAddressFromList',
        ]),
        properties: {
          placeholder: '123 Front St',
          minCharMatchCount: 1,
          choices: googleAddressMatches.map(({ description }) => description),
          onSuggestionsFetchRequested: ({ value }) => {
            return onFindGoogleListings({ ...values, addressStreetDisplay: value });
          },
          onChange: onGoogleAddressChange,
          filterFunc: () => {
            return true;
          },
        },
      },
      {
        id: 'apartmentUnit',
        title: 'Unit #',
        titleSecondary: '(optional)',
        type: 'short_text',
        properties: {
          placeholder: 'Unit #',
        },
      },
      {
        id: 'price',
        title: 'Rent price',
        type: 'currency',
        properties: {
          placeholder: '$1990',
        },
        validations: ['required'],
      },
      {
        id: 'availableDate',
        title: 'Available date for move-in',
        type: 'date_picker',
        properties: {
          placeholder: 'Earliest date that renters can move-in...',
          dateFormat: 'MM/DD/YYYY',
          readOnly: false,
          isValidDate: (date) => date.isAfter(momentWithAustinTimezone().subtract(1, 'days')),
        },
        validations: ['required'],
      },
      {
        id: 'matchAddress',
        type: 'hidden',
      },
    ];
  };
}

class SrTenantScreeningPropertyInformationModal extends Component {
  static propTypes = {
    onSubmit: PropTypes.func.isRequired,
    dispatch: PropTypes.func.isRequired,
    partnerSlug: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    errorMessage: PropTypes.string,
  }

  constructor(props) {
    super(props);
    this.state = {
      googleAddressMatches: [],
    };
    this._onFindGoogleListings = this._onFindGoogleListings.bind(this);
  }

  _onFindGoogleListings(formValues) {
    const { dispatch } = this.props;
    if (
      !formValues.addressStreetDisplay
    ) {
      this.setState({
        googleAddressMatches: [],
      });
      return;
    }

    dispatch(postWithState, {
      queryId: formValues.addressStreetDisplay,
      action: SR_API_ACTION_GOOGLE_MAPS_GET_AUTOCOMPLETE_PREDICTIONS,
      payload: {
        input: formValues.addressStreetDisplay,
        limitToTexas: false,
      },
    })
      .then((autocompletePredictions) => {
        this.setState({
          googleAddressMatches: autocompletePredictions,
        });
      });
  }

  _onCancelScreening = () => {
    const { history, partnerSlug } = this.props;
    history.push(`/partner/${partnerSlug}`);
  }

  _onGoogleAddressChange = ({ newValue, formikBag }) => {
    const { googleAddressMatches } = this.state;
    const selectedMatch = (
      googleAddressMatches?.find(({ description }) => description === newValue)
    );
    if (selectedMatch?.place_id) {
      formikBag.setFieldValue('matchAddress', selectedMatch);
    }
    else {
      formikBag.setFieldValue('matchAddress', null);
    }
  }

  render() {
    const {
      googleAddressMatches,
    } = this.state;

    const {
      onSubmit,
      errorMessage,
    } = this.props;

    const getFormFields = makeFormFieldsGetterFunc({
      googleAddressMatches: googleAddressMatches,
      onGoogleAddressChange: this._onGoogleAddressChange,
      onFindGoogleListings: this._onFindGoogleListings,
    });

    return (
      <SrModalV2
        showCloseButton={false}
        contentMaxWidth='587px'
        contentPadding='25px 53px 40px'
        theme={MODAL_THEME_STACK}
      >
        <SrBox>
          <SrFlex align='center' direction='col'>
            <SrIcon className='property-illustration' />
            <SrBox height='10px' />
            <SrText
              font='greycliff'
              weight='800'
              size='26'
              lineHeight='33px'
              align='center'
              width='100%'
              display='block'
            >
              Add a property to get <br /> the application link.
            </SrText>
          </SrFlex>
          <SrBox height='9px' />
          <Formik
            validateOnChange
            initialValues={createInitialValues({
              formFields: getFormFields({ values: {} }),
              defaultValues: {},
            })}
            onSubmit={onSubmit}
            validate={(values) => {
              return createFormValidationFn(getFormFields({
                values,
              }))(values);
            }}
            render={(formikBag) => {
              const formFields = getFormFields({
                values: formikBag.values,
              });
              return (
                <Form>
                  {formFields.map((formField, index) => (
                    <SrFormikField
                      key={formField.id}
                      formField={formField}
                      formikBag={formikBag}
                      theme={SR_FIELD_THEME_ADOBE}
                      margin={index === 0 ? '0px' : null}
                      className={SR_FIELD_THEME_PARTNER_FORM}
                    />
                  ))}
                  {errorMessage &&
                    <SrBox
                      paddingTop='10px'
                      paddingBottom='10px'
                      paddingLeft='8px'
                      paddingRight='5px'
                      bgColor='red'
                      borderRadius='8px'
                    >
                      <SrText
                        fontSize='14px'
                        color='white'
                        weight='600'
                        font='greycliff'
                      >
                        {errorMessage}
                      </SrText>
                    </SrBox>
                  }
                  <SrBox padding='23px 0 16px'>
                    <SrButton
                      className='save'
                      theme={SR_BUTTON_THEME_ADOBE_BLUE}
                      onClick={formikBag.handleSubmit}
                      disabled={!formikBag.isValid}
                    >
                      Continue
                    </SrButton>
                  </SrBox>
                </Form>
              );
            }}
          />
          <SrButton
            theme={SR_BUTTON_THEME_ADOBE_GREY}
            onClick={this._onCancelScreening}
          >
            Cancel
          </SrButton>
        </SrBox>
      </SrModalV2>
    );
  }
}

SrTenantScreeningPropertyInformationModal.displayName = 'SrTenantScreeningPropertyInformationModal';

export default SrTenantScreeningPropertyInformationModal;
